import type { Moment } from "moment-timezone";
import { useState } from "react";
import {
	UTILITY_CONSUMPTION_REPORT_MODE,
	type UtilityConsumptionDirection,
	type UtilityConsumptionReportCalendarMode,
	type UtilityConsumptionReportMode,
	type UtilityConsumptionReportRange,
} from "../models/UtilityConsumption";
import {
	getCurrentUtilityConsumptionReportDate,
	getDefaultUtilityConsumptionSectionRange,
	moveUtilityConsumptionReportDate,
} from "../utils/calendar";

interface UseConsumptionReportPeriodInput {
	plantTimeZone: string;
	range: UtilityConsumptionReportRange;
	onRangeChange: (range: UtilityConsumptionReportRange) => void;
}

export interface ConsumptionReportPeriod {
	mode: UtilityConsumptionReportMode;
	isSection: boolean;
	referenceDate: Date;
	sectionEndBoundary: Date;
	sectionRange: UtilityConsumptionReportRange;
	activeRange: UtilityConsumptionReportRange;
	calendarMode: UtilityConsumptionReportCalendarMode | null;
	plantTimeZone: string;
	changeMode: (mode: UtilityConsumptionReportMode) => void;
	setReferenceDate: (referenceDate: Date) => void;
	setRangeDate: (
		field: keyof UtilityConsumptionReportRange,
		value: Moment | null,
	) => void;
	stepCalendar: (direction?: UtilityConsumptionDirection) => void;
}

export const useConsumptionReportPeriod = ({
	plantTimeZone,
	range,
	onRangeChange,
}: UseConsumptionReportPeriodInput): ConsumptionReportPeriod => {
	const [mode, setMode] = useState<UtilityConsumptionReportMode>(
		UTILITY_CONSUMPTION_REPORT_MODE.PERIOD,
	);
	const [referenceDate, setReferenceDate] = useState(() => new Date());
	const [sectionEndBoundary] = useState(() => new Date());
	const [sectionRange, setSectionRange] = useState(() =>
		getDefaultUtilityConsumptionSectionRange(plantTimeZone),
	);

	const isSection = mode === UTILITY_CONSUMPTION_REPORT_MODE.SECTION;
	const calendarMode: UtilityConsumptionReportCalendarMode | null =
		mode === UTILITY_CONSUMPTION_REPORT_MODE.PERIOD || isSection ? null : mode;
	const activeRange = isSection ? sectionRange : range;

	return {
		mode,
		isSection,
		referenceDate,
		sectionEndBoundary,
		sectionRange,
		activeRange,
		calendarMode,
		plantTimeZone,
		changeMode: (nextMode) => {
			setMode(nextMode);
			if (
				nextMode !== UTILITY_CONSUMPTION_REPORT_MODE.PERIOD &&
				nextMode !== UTILITY_CONSUMPTION_REPORT_MODE.SECTION
			) {
				setReferenceDate(
					getCurrentUtilityConsumptionReportDate(nextMode, plantTimeZone),
				);
			}
		},
		setReferenceDate,
		setRangeDate: (field, value) => {
			if (!value?.isValid()) return;
			const nextRange = { ...activeRange, [field]: value.format("YYYY-MM-DD") };
			if (isSection) setSectionRange(nextRange);
			else onRangeChange(nextRange);
		},
		stepCalendar: (direction) => {
			if (calendarMode === null) return;
			setReferenceDate(
				direction === undefined
					? getCurrentUtilityConsumptionReportDate(calendarMode, plantTimeZone)
					: moveUtilityConsumptionReportDate(
							calendarMode,
							referenceDate,
							direction,
							plantTimeZone,
						),
			);
		},
	};
};
